/**
 * "+ New task": the dispatch form in a right-hand drawer, over the Tasks
 * table rather than above it.
 *
 * Above the table, the ~300px form left a MacBook Air (1440x800; 1280x720
 * on its smaller setting -- the staff's own machine at sg-nomad-01,
 * 2026-10-22) one task row, and folding it away meant the table jumped
 * every time it opened. In a drawer the table keeps its whole height and
 * stays readable to the left while a task is written.
 *
 * The task detail drawer's shell, size and manners: 420px on the right,
 * Escape, ✕, or a press outside it closes it.
 *
 * CLOSED IS HIDDEN, NOT GONE. The drawer stays mounted, so a half-written
 * task is still there when it is opened again -- closing is a press away
 * from any click on the table, and must not cost the person their form.
 *
 * The form's footer is already `position: sticky` (`TaskDispatchForm.css`),
 * and the drawer body is the thing that scrolls, so on a short screen
 * Dispatch and Save as favourite stay in view under a scrolling form.
 *
 * THE SAME SHELL EDITS A SAVED SCHEDULE (`ScheduleEditDrawer`, 2026-10-30):
 * a title and test id of its own, the same size, focus and dismissal.
 *
 * ABOVE AN ON-SCREEN KEYBOARD. The front desk's iPad in portrait gets
 * these drawers (the phone calendar is below 768px only), and a
 * keyboard opened by a name or zone field covers the bottom of a
 * full-height drawer -- which is where its Save is. While the visual
 * viewport is shorter than the window (a keyboard is up), the drawer is
 * sized and placed to the visual viewport, so its sticky footer sits
 * just above the keys instead of under them.
 */
import { useCallback, useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { useDismissOnOutside } from '../../shell/use-dismiss-on-outside';
import './TaskDetailDrawer.css';
import './NewTaskDrawer.css';

/** Stable, so the dismiss hook does not re-subscribe on every render
 *  while the drawer is closed (the `FavoritesMenu` idiom). */
const NOT_OPEN: RefObject<HTMLElement | null> = { current: null };

export function NewTaskDrawer({
  open,
  onClose,
  triggerRef,
  children,
  title = 'New task',
  id = 'nx-tasks-dispatch',
  testId = 'tasks-dispatch-drawer',
}: {
  open: boolean;
  onClose: () => void;
  /** "+ New task": outside the drawer, so pressing it must toggle rather
   *  than count as an outside press that closes and reopens it. */
  triggerRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  /** The heading and the dialog's name. */
  title?: string;
  id?: string;
  /** The drawer's test id; its ✕ is `<testId>-close`. */
  testId?: string;
}) {
  const panelRef = useRef<HTMLElement>(null);

  // Escape and ✕ hand focus back to "+ New task"; a press elsewhere
  // leaves it where the person put it.
  const closeAndReturnFocus = useCallback(() => {
    const inside = panelRef.current?.contains(document.activeElement) ?? false;
    onClose();
    if (inside) triggerRef.current?.focus();
  }, [onClose, triggerRef]);

  useDismissOnOutside(open ? panelRef : NOT_OPEN, open ? closeAndReturnFocus : noop, triggerRef);

  // Opening moves focus into the form, to its first field.
  useEffect(() => {
    if (!open) return;
    const first = panelRef.current?.querySelector<HTMLElement>(
      '.nx-new-task-drawer__body select, .nx-new-task-drawer__body input',
    );
    (first ?? panelRef.current)?.focus();
  }, [open]);

  useKeyboardSafeBox(open, panelRef);

  return (
    <aside
      ref={panelRef}
      id={id}
      className="nx-task-drawer nx-new-task-drawer"
      role="dialog"
      aria-label={title}
      hidden={!open}
      tabIndex={-1}
      data-testid={testId}
    >
      <header className="nx-task-drawer__head">
        <h2>{title}</h2>
        <button
          type="button"
          aria-label="Close"
          onClick={closeAndReturnFocus}
          data-testid={`${testId}-close`}
        >
          ×
        </button>
      </header>
      <div className="nx-task-drawer__body nx-new-task-drawer__body">{children}</div>
    </aside>
  );
}

function noop() {}

/**
 * While open, keep `ref` inside the VISUAL viewport when it is shorter
 * than the window -- an on-screen keyboard is up -- and back to its
 * stylesheet size when it is not. Nothing at all where the browser has
 * no `visualViewport` (jsdom, old engines): the drawer is then exactly
 * the full-height box it always was.
 */
function useKeyboardSafeBox(open: boolean, ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const el = ref.current;
    if (!open || !vv || !el) return;
    const fit = () => {
      const shrunk = vv.height < window.innerHeight - 1;
      el.style.top = shrunk ? `${Math.round(vv.offsetTop)}px` : '';
      el.style.height = shrunk ? `${Math.round(vv.height)}px` : '';
    };
    fit();
    vv.addEventListener('resize', fit);
    vv.addEventListener('scroll', fit);
    return () => {
      vv.removeEventListener('resize', fit);
      vv.removeEventListener('scroll', fit);
      el.style.top = '';
      el.style.height = '';
    };
  }, [open, ref]);
}
